import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { EquipmentTestLibraryEntry, ImageOption } from '@rock/types';
import type { FC } from 'react';

/**
 * One entry, near-full-screen: both photos side by side, each given half the window and letterboxed
 * on black.
 *
 * The size is the point. A phone running the app is pointed at this screen to scan the nameplate,
 * which means on-device OCR reading a photo *of a display* — pixels and contrast are the whole
 * ballgame, and a plate shrunk into a card-sized pane is the difference between a scan that reads
 * and one that doesn't.
 *
 * Viewing only — every mutation lives on the form dialog, which Edit hands off to (and which is also
 * reachable straight from the card's hover Edit, without opening this).
 */
export const EntryDetailDialog: FC<{
    entry: EquipmentTestLibraryEntry | undefined;
    onOpenChange: (open: boolean) => void;
    onEdit: () => void;
}> = ({ entry, onOpenChange, onEdit }) => {
    if (!entry) return null;

    const subtitle =
        [
            entry.brand,
            entry.modelNumber && `Model ${entry.modelNumber}`,
            entry.serialNumber && `Serial ${entry.serialNumber}`,
        ]
            .filter(Boolean)
            .join(' · ') || 'No brand or numbers recorded';

    return (
        <Dialog open={!!entry} onOpenChange={onOpenChange}>
            {/* The shared DialogContent is `grid` with an `sm:max-w-lg` cap, so this works WITH the
                grid (explicit rows) and needs the `sm:` prefix to beat that cap rather than a bare
                `max-w-none`, which loses to the responsive variant. */}
            <DialogContent className="h-[90vh] w-[90vw] grid-rows-[auto_minmax(0,1fr)_auto] gap-3 sm:max-w-none">
                <DialogHeader className="shrink-0">
                    <DialogTitle className="flex items-center gap-2">
                        {entry.name}
                        {entry.source === 'web-sourced' && <Badge variant="secondary">Web-sourced</Badge>}
                        {entry.outdated && <Badge variant="secondary">Outdated</Badge>}
                    </DialogTitle>
                    <DialogDescription>{subtitle}</DialogDescription>
                </DialogHeader>

                <div className="grid min-h-0 grid-cols-1 gap-3 md:grid-cols-2">
                    <PhotoPane label="Product" image={entry.productImage} />
                    <PhotoPane label="Nameplate" image={entry.nameplateImage} />
                </div>

                <div className="flex shrink-0 items-start justify-between gap-4">
                    <div className="text-sm text-muted-foreground">{entry.notes}</div>
                    <Button type="button" variant="outline" size="sm" onClick={onEdit}>
                        Edit
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const PhotoPane: FC<{ label: string; image: ImageOption | undefined }> = ({ label, image }) => (
    <div className="flex min-h-0 flex-col gap-1">
        <span className="shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded bg-black">
            {image ? (
                // Letterboxed, never cropped to fill: a plate cropped to fill would lose the very
                // characters the scan is after.
                <img src={image.url} alt={label} className="max-h-full max-w-full object-contain" />
            ) : (
                <span className="text-sm text-white/50">No {label.toLowerCase()} photo</span>
            )}
        </div>
    </div>
);
